"use client";

import posthog from "posthog-js";
import { PostHogProvider as PostHogProviderRaw } from "posthog-js/react";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { keys } from "../keys";

type PostHogProviderProps = {
  readonly children: ReactNode;
};

/**
 * Starts PostHog once the browser is idle rather than on first paint.
 *
 * Initialising inside the mount effect put the flags request and the config
 * request on the critical path of every hard page load. Nothing the person is
 * waiting for needs analytics, so the work is handed to `requestIdleCallback`,
 * with a timer for the browsers that do not have it (Safari only shipped it
 * recently) and a deadline so a permanently busy page still reports.
 *
 * Reading `posthog` before it starts is safe: the library keeps its singleton
 * usable and answers an early `capture()` with a console warning rather than an
 * exception, and the provider below hands consumers that same singleton from the
 * first render either way.
 */
export const PostHogProvider = (properties: Omit<PostHogProviderProps, "client">) => {
  useEffect(() => {
    const start = () => {
      // The scheduled callback is cancelled on unmount, so this guard is only
      // about a second mount reaching an already-started client.
      if (posthog.__loaded) {
        return;
      }
      posthog.init(keys().NEXT_PUBLIC_POSTHOG_KEY, {
        api_host: "/ingest",
        ui_host: keys().NEXT_PUBLIC_POSTHOG_HOST,
        person_profiles: "identified_only",
        capture_pageview: false, // Disable automatic pageview capture, as we capture manually
        capture_pageleave: true, // Overrides the `capture_pageview` setting
      });
    };

    if (typeof window.requestIdleCallback === "function") {
      const handle = window.requestIdleCallback(start, { timeout: 4000 });
      return () => window.cancelIdleCallback?.(handle);
    }

    const timer = window.setTimeout(start, 2000);
    return () => window.clearTimeout(timer);
  }, []);

  return <PostHogProviderRaw client={posthog} {...properties} />;
};

export { usePostHog as useAnalytics } from "posthog-js/react";
